import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetRawMaterials,
  CreateRawMaterial,
  UpdateRawMaterial,
  ToggleRawMaterialStatus,
  UpdateRawMaterialStockStatus,
  DeleteRawMaterial,
  GetRawPurchases,
  GetRawPurchaseById,
  CreateRawPurchase,
  UpdateRawPurchase,
  DeleteRawPurchase,
  GetRecipe,
  SaveRecipe,
  DeleteRecipe,
  RecordRawPurchasePayment,
  GetRawMaterialSupplierPayments,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME, STALE_TIME_0 } from "../Constant/AppConstants";

export const rawMaterialQueryKeys = {
  rawMaterials: (params?: Parameters<typeof GetRawMaterials>[0]) =>
    ["rawMaterials", params] as const,
  rawPurchases: (params?: Parameters<typeof GetRawPurchases>[0]) =>
    ["rawPurchases", params] as const,
  rawPurchaseById: (params?: Parameters<typeof GetRawPurchaseById>[0]) =>
    ["rawPurchaseById", params] as const,
  recipe: (params?: Parameters<typeof GetRecipe>[0]) =>
    ["recipe", params] as const,
};

export const invalidateRawMaterialQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["rawMaterials"] });
  queryClient.invalidateQueries({ queryKey: ["rawPurchases"] });
  queryClient.invalidateQueries({ queryKey: ["rawPurchaseById"] });
  queryClient.invalidateQueries({ queryKey: ["recipe"] });
};

export const useRawMaterialsQuery = (
  params: Parameters<typeof GetRawMaterials>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: STALE_TIME_0,
    ...options,
    queryKey: rawMaterialQueryKeys.rawMaterials(params),
    queryFn: async () => {
      const res = await GetRawMaterials(params);
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useRawMaterialsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetRawMaterials>[0]) =>
      queryClient.fetchQuery({
        staleTime: STALE_TIME_0,
        queryKey: rawMaterialQueryKeys.rawMaterials(params),
        queryFn: async () => {
          const res = await GetRawMaterials(params);
          return {
            data: res.data || [],
            total: res.meta?.total || 0,
          };
        },
      }),
  });
};

export const useRawPurchasesQuery = (
  params: Parameters<typeof GetRawPurchases>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: STALE_TIME_0,
    ...options,
    queryKey: rawMaterialQueryKeys.rawPurchases(params),
    queryFn: async () => unwrapApiResponse(await GetRawPurchases(params)),
  });
};

export const useRawPurchasesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetRawPurchases>[0]) =>
      queryClient.fetchQuery({
        staleTime: STALE_TIME_0,
        queryKey: rawMaterialQueryKeys.rawPurchases(params),
        queryFn: async () => unwrapApiResponse(await GetRawPurchases(params)),
      }),
  });
};

export const useRawPurchaseByIdQuery = (
  params: Parameters<typeof GetRawPurchaseById>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: rawMaterialQueryKeys.rawPurchaseById(params),
    queryFn: async () => unwrapApiResponse(await GetRawPurchaseById(params)),
  });
};

export const useRawPurchaseByIdRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetRawPurchaseById>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: rawMaterialQueryKeys.rawPurchaseById(params),
        queryFn: async () =>
          unwrapApiResponse(await GetRawPurchaseById(params)),
      }),
  });
};

export const useRecipeQuery = (
  params?: Parameters<typeof GetRecipe>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: rawMaterialQueryKeys.recipe(params),
    queryFn: async () => unwrapApiResponse(await GetRecipe(params)),
  });
};

export const useRecipeRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: Parameters<typeof GetRecipe>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: rawMaterialQueryKeys.recipe(params),
        queryFn: async () => unwrapApiResponse(await GetRecipe(params)),
      }),
  });
};

export const useCreateRawMaterialMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateRawMaterial>[0]) =>
      CreateRawMaterial(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useUpdateRawMaterialMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateRawMaterial>[0]) =>
      UpdateRawMaterial(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useToggleRawMaterialStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      id: Parameters<typeof ToggleRawMaterialStatus>[0];
      status: Parameters<typeof ToggleRawMaterialStatus>[1];
    }) => ToggleRawMaterialStatus(payload.id, payload.status),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useUpdateRawMaterialStockStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateRawMaterialStockStatus>[0]) =>
      UpdateRawMaterialStockStatus(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useDeleteRawMaterialMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteRawMaterial>[0]) =>
      DeleteRawMaterial(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useCreateRawPurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateRawPurchase>[0]) =>
      CreateRawPurchase(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useUpdateRawPurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateRawPurchase>[0]) =>
      UpdateRawPurchase(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useDeleteRawPurchaseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteRawPurchase>[0]) =>
      DeleteRawPurchase(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useRecordRawPurchasePaymentMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof RecordRawPurchasePayment>[0]) =>
      RecordRawPurchasePayment(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useRawMaterialSupplierPaymentsQuery = (
  params: Parameters<typeof GetRawMaterialSupplierPayments>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: ["rawMaterialSupplierPayments", params] as const,
    queryFn: async () =>
      unwrapApiResponse(await GetRawMaterialSupplierPayments(params)),
  });
};

export const useSaveRecipeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof SaveRecipe>[0]) =>
      SaveRecipe(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useDeleteRecipeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteRecipe>[0]) =>
      DeleteRecipe(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRawMaterialQueries(queryClient);
      }
    },
  });
};

export const useRawMaterialService = (config?: {
  fetchRawMaterialsParams?: Parameters<typeof GetRawMaterials>[0];
  fetchRawMaterialsEnabled?: boolean;
  fetchRawPurchasesParams?: Parameters<typeof GetRawPurchases>[0];
  fetchRawPurchasesEnabled?: boolean;
  fetchRawPurchaseByIdParams?: Parameters<typeof GetRawPurchaseById>[0];
  fetchRawPurchaseByIdEnabled?: boolean;
  fetchRecipeParams?: Parameters<typeof GetRecipe>[0];
  fetchRecipeEnabled?: boolean;
}) => {
  const rawMaterialsQuery = useRawMaterialsQuery(
    config?.fetchRawMaterialsParams,
    {
      enabled:
        config?.fetchRawMaterialsEnabled !== false &&
        config?.fetchRawMaterialsParams !== undefined &&
        config?.fetchRawMaterialsParams !== null,
    },
  );

  const rawPurchasesQuery = useRawPurchasesQuery(
    config?.fetchRawPurchasesParams,
    {
      enabled:
        config?.fetchRawPurchasesEnabled !== false &&
        config?.fetchRawPurchasesParams !== undefined &&
        config?.fetchRawPurchasesParams !== null,
    },
  );

  const rawPurchaseByIdQuery = useRawPurchaseByIdQuery(
    config?.fetchRawPurchaseByIdParams,
    {
      enabled:
        config?.fetchRawPurchaseByIdEnabled !== false &&
        config?.fetchRawPurchaseByIdParams !== undefined &&
        config?.fetchRawPurchaseByIdParams !== null,
    },
  );

  const recipeQuery = useRecipeQuery(config?.fetchRecipeParams, {
    enabled: config?.fetchRecipeEnabled !== false,
  });

  const createRawMaterialMutation = useCreateRawMaterialMutation();
  const updateRawMaterialMutation = useUpdateRawMaterialMutation();
  const toggleRawMaterialStatusMutation = useToggleRawMaterialStatusMutation();
  const deleteRawMaterialMutation = useDeleteRawMaterialMutation();
  const createRawPurchaseMutation = useCreateRawPurchaseMutation();
  const updateRawPurchaseMutation = useUpdateRawPurchaseMutation();
  const deleteRawPurchaseMutation = useDeleteRawPurchaseMutation();
  const saveRecipeMutation = useSaveRecipeMutation();
  const deleteRecipeMutation = useDeleteRecipeMutation();

  return {
    rawMaterialsQuery,
    rawPurchasesQuery,
    rawPurchaseByIdQuery,
    recipeQuery,
    createRawMaterialMutation,
    updateRawMaterialMutation,
    toggleRawMaterialStatusMutation,
    deleteRawMaterialMutation,
    createRawPurchaseMutation,
    updateRawPurchaseMutation,
    deleteRawPurchaseMutation,
    saveRecipeMutation,
    deleteRecipeMutation,
  };
};
